import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from './states';

interface Props {
  children: ReactNode;
  /** Đổi giá trị (vd. đường dẫn trang) thì bỏ lỗi cũ, vẽ lại nội dung. */
  resetKey?: unknown;
  title?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  resetKey: unknown;
}

/**
 * Lỗi khi vẽ giao diện (vd. "x is not a function") không làm trắng cả ứng dụng: hiện thông báo,
 * nút tải lại và chi tiết kỹ thuật (tên file .js, dòng, component) để chụp gửi quản trị.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, componentStack: null, resetKey: props.resetKey };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  override render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;
    return (
      <div data-testid="error-boundary">
        <ErrorState error={error} title={this.props.title ?? 'Trang này gặp lỗi khi hiển thị'} onRetry={() => window.location.reload()} />
        <details className="mx-auto mb-8 max-w-3xl px-4 text-xs text-muted-foreground">
          <summary className="cursor-pointer text-center">Chi tiết kỹ thuật (chụp gửi quản trị)</summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded border bg-muted/40 p-2 text-left whitespace-pre-wrap">
            {[`${error.name}: ${error.message}`, `Trang: ${window.location.pathname}`, error.stack, componentStack].filter(Boolean).join('\n\n')}
          </pre>
        </details>
      </div>
    );
  }
}
